/**
 * Budget plan card — one rolling plan with progress, categories, and actions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Eye, Pencil, Trash2, ArrowRight } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import { useDeleteBudget } from '../../hooks/useBudgetOverview';
import type { Budget, BudgetCategoryStatus } from '../../types/budget.types';
import { deriveCategoryStatus, DEFAULT_ALERT_THRESHOLDS } from '../../utils/budget-calculations';

interface BudgetPlanCardProps {
  budget: Budget;
  onViewDetails: (budget: Budget) => void;
  onEdit: (budget: Budget) => void;
}

function overallHealth(budget: Budget): BudgetCategoryStatus {
  const thresholds = budget.alertThresholds ?? DEFAULT_ALERT_THRESHOLDS;
  const limits = (budget.categoryLimits ?? []).filter(
    (l) => (l.planAmount ?? l.amount) > 0
  );
  if (limits.length === 0) {
    return deriveCategoryStatus(budget.percentUsed ?? 0, thresholds);
  }
  const statuses = limits.map(
    (l) => l.status ?? deriveCategoryStatus(l.percentUsed ?? 0, thresholds)
  );
  if (statuses.includes('over')) return 'over';
  if (statuses.includes('critical')) return 'critical';
  if (statuses.includes('warning')) return 'warning';
  return 'on_track';
}

function healthLabel(status: BudgetCategoryStatus): string {
  switch (status) {
    case 'over':
      return 'Over';
    case 'critical':
      return 'Critical';
    case 'warning':
      return 'Warning';
    default:
      return 'On track';
  }
}

function healthBadgeClass(status: BudgetCategoryStatus): string {
  switch (status) {
    case 'over':
      return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    case 'critical':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
    default:
      return 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
  }
}

function progressBarColor(status: BudgetCategoryStatus, percentUsed: number): string {
  if (status === 'over' || percentUsed >= 100) return 'bg-red-500';
  if (status === 'critical') return 'bg-orange-500';
  if (status === 'warning') return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Build /transactions query for this plan's evaluation period + category limits. */
export function buildBudgetTransactionsHref(budget: Budget): string {
  const params = new URLSearchParams();
  if (budget.periodStart) params.set('dateFrom', budget.periodStart);
  if (budget.periodEnd) params.set('dateTo', budget.periodEnd);
  const categoryIds = (budget.categoryLimits ?? [])
    .map((l) => l.categoryId)
    .filter(Boolean);
  if (categoryIds.length > 0) {
    params.set('categories', categoryIds.join(','));
  }
  const qs = params.toString();
  return qs ? `/transactions?${qs}` : '/transactions';
}

function periodGrainLabel(periodType: Budget['periodType']): string {
  if (periodType === 'weekly') return 'week';
  if (periodType === 'yearly') return 'year';
  return 'month';
}

export function BudgetPlanCard({
  budget,
  onViewDetails,
  onEdit,
}: BudgetPlanCardProps): React.ReactElement {
  const { formatCurrency } = useRegion();
  const deleteBudget = useDeleteBudget();

  const percentUsed = budget.percentUsed ?? 0;
  const percentRemaining = Math.max(0, 100 - percentUsed);
  const totalBudget = budget.totalBudget ?? 0;
  const planTotalBudget = budget.planTotalBudget ?? totalBudget;
  const showsScaledBudget = planTotalBudget !== totalBudget;
  const totalSpent = budget.totalSpent ?? 0;
  const totalRemaining = budget.totalRemaining ?? totalBudget - totalSpent;
  const limits = budget.categoryLimits ?? [];
  const health = overallHealth(budget);
  const onTrackCount = limits.filter((l) => (l.status ?? 'on_track') === 'on_track').length;
  const transactionsHref = buildBudgetTransactionsHref(budget);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${budget.name}"? This cannot be undone.`)) return;
    try {
      await deleteBudget.mutateAsync(budget.id);
    } catch (e) {
      console.error('Failed to delete budget:', e);
    }
  };

  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
            <Wallet className="h-5 w-5 text-purple-600 dark:text-purple-400" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {budget.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {budget.periodLabel ?? 'This period'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full ${healthBadgeClass(health)}`}>
            {healthLabel(health)}
          </span>
        </div>
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        <p>
          <span className="text-gray-900 dark:text-gray-100 font-medium">
            Budget: {formatCurrency(totalBudget)}
          </span>
          <span className="mx-1.5 text-gray-400">·</span>
          <span>Spent: {formatCurrency(totalSpent)}</span>
          <span className="mx-1.5 text-gray-400">·</span>
          <span
            className={
              totalRemaining >= 0
                ? 'text-green-700 dark:text-green-400 font-medium'
                : 'text-red-600 dark:text-red-400 font-medium'
            }
          >
            Remaining: {formatCurrency(totalRemaining)}
          </span>
        </p>
        {showsScaledBudget && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Plan limit: {formatCurrency(planTotalBudget)} per {periodGrainLabel(budget.periodType)}
          </p>
        )}
      </div>

      <div className="mb-4">
        <div
          className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5"
          role="progressbar"
          aria-valuenow={percentUsed}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${budget.name} progress`}
        >
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${progressBarColor(health, percentUsed)}`}
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-xs">
          <span className="text-gray-500 dark:text-gray-400">{percentUsed}% used this period</span>
          <span
            className={
              totalRemaining >= 0
                ? 'text-green-600 dark:text-green-400 font-medium'
                : 'text-red-600 dark:text-red-400 font-medium'
            }
          >
            {percentRemaining}% remaining
          </span>
        </div>
      </div>

      <div className="flex-1 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Categories</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {onTrackCount}/{limits.length} on track
          </span>
        </div>
        {limits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No category limits</p>
        ) : (
          <ul className="space-y-1.5">
            {limits.map((limit) => (
              <li
                key={limit.id}
                className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="truncate pr-3">{limit.categoryName ?? 'Unknown'}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 shrink-0 text-right">
                  {formatCurrency(limit.amount)}
                  {limit.planAmount != null && limit.planAmount !== limit.amount && (
                    <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                      {formatCurrency(limit.planAmount)}/{periodGrainLabel(budget.periodType)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onEdit(budget)}
            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={`Edit ${budget.name}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onViewDetails(budget)}
            className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={`View details for ${budget.name}`}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleteBudget.isPending}
            className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            aria-label={`Delete ${budget.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <Link
          to={transactionsHref}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 transition-colors"
        >
          View Transactions
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </article>
  );
}
